import { useEffect, useState } from 'react';
import logoUrl from '/logo.png';
import AccountWidget from './AccountWidget';

/**
 * ⚠️ SITE CHROME: KEEP IN SYNC across products.
 * The pagecoder.ai header/footer is DUPLICATED because AI Visibility Scan is a
 * standalone app that can't reuse the theme's React chrome. If you change the
 * header/footer (nav items, logo, links, styling), update ALL copies:
 *   1. pagecoder (slidecoder theme): src/components/Header.tsx + Footer.tsx  ← canonical
 *   2. pagecoder-aio-scan: frontend/src/components/SiteHeader.tsx + SiteFooter.tsx  ← this
 *   (pagecoder-rag-chat is a WP plugin, no own chrome; its pages use the theme.)
 *
 * Site header matching pagecoder.ai (slidecoder theme). AI Visibility Scan is a
 * standalone app, so it renders its own copy of the site chrome (D-23). "AI
 * Visibility Scan" is the active/first nav item; other items link back to the
 * main site; "Pricing" is intentionally absent (RAG Chat pricing lives on the
 * RAG Chat page; AIVS pricing is in-app).
 */
const NAV = [
  { href: '/ai-visibility-scan', label: 'AI Visibility Scan', active: true },
  { href: 'https://pagecoder.ai/products/rag-chat', label: 'RAG Chat' },
  { href: 'https://pagecoder.ai/privacy-tour', label: 'Privacy Tour' },
  { href: 'https://pagecoder.ai/blog', label: 'Blog' },
  // Mirrors the theme's `navItems` order exactly (Header.tsx). "Work with me"
  // is where a visitor finds out this is one person they can hire, which is the
  // whole point of D94; leaving it out of this header made the app look like a
  // product from a company with no people in it.
  { href: 'https://pagecoder.ai/about', label: 'Work with me' },
  { href: 'https://pagecoder.ai/contact', label: 'Contact' },
];

export default function SiteHeader(): JSX.Element {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = (): void => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-40">
        <div className={`backdrop-blur-md transition-colors duration-300 ${scrolled ? 'bg-warm-dark/85 border-b border-white/5' : 'bg-transparent'}`}>
          <div className="max-w-7xl mx-auto px-6 h-14 flex items-center justify-between">
            <a href="https://pagecoder.ai/" className="inline-flex items-center gap-2 font-semibold text-xl hover:opacity-80 transition-opacity" style={{ fontFamily: "'Inter', system-ui, sans-serif", letterSpacing: '-0.015em' }}>
              <img src={logoUrl} alt="" width={28} height={28} className="shrink-0" aria-hidden="true" />
              <span className="text-text-primary">PageCoder</span>
              <span className="text-text-secondary">.ai</span>
            </a>

            <nav className="hidden md:flex items-center gap-1">
              {NAV.map((item) => (
                <a
                  key={item.href}
                  href={item.href}
                  className={`font-mono text-sm px-3 py-2 rounded transition-colors ${item.active ? 'text-cyan' : 'text-text-secondary hover:text-text-primary'}`}
                >
                  {item.label}
                </a>
              ))}
              <span className="ml-3"><AccountWidget /></span>
            </nav>

            <div className="md:hidden flex items-center gap-3">
              <AccountWidget />
              <button onClick={() => setOpen(true)} className="w-9 h-9 flex items-center justify-center text-text-primary" aria-label="Open menu">☰</button>
            </div>
          </div>
        </div>
      </header>
      <div className="h-14" />

      {open && (
        <div className="fixed inset-0 z-50 bg-warm-dark/98 backdrop-blur-lg md:hidden">
          <div className="flex items-center justify-between h-14 px-6 border-b border-white/5">
            <span className="font-mono text-sm text-text-primary flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-cyan" style={{ boxShadow: '0 0 8px #00FFD4' }} /> pagecoder
            </span>
            <button onClick={() => setOpen(false)} className="w-9 h-9 flex items-center justify-center text-text-primary" aria-label="Close menu">✕</button>
          </div>
          <nav className="px-6 py-8 space-y-1">
            {NAV.map((item) => (
              <a key={item.href} href={item.href} onClick={() => setOpen(false)} className={`block font-mono text-base px-2 py-3 ${item.active ? 'text-cyan' : 'text-text-primary'}`}>
                {item.label}
              </a>
            ))}
          </nav>
        </div>
      )}
    </>
  );
}
